'use client'

import { createContext, type ReactNode, useContext, useEffect, useState } from 'react'
import { APP_BRAND_NAME } from '@/consts/branding'
import { InboxMessage, MessageStatus } from '@/types/company'

interface InboxContextType {
    messages: InboxMessage[]
    unreadCount: number
    updateMessageStatus: (id: string, status: MessageStatus) => void
    getMessagesByStatus: (status: MessageStatus) => InboxMessage[]
}

// Mock messages for demo
const mockMessages: InboxMessage[] = [
    {
        id: '1',
        studentName: 'Lena Müller',
        studentEmail: 'lena.mueller@schule.de',
        schoolType: 'Gymnasium',
        graduationYear: 2025,
        interestedJob: 'Industriekaufmann/-frau',
        message:
            'Sehr geehrte Damen und Herren, ich interessiere mich sehr für eine Ausbildung in Ihrem Unternehmen. Besonders die Kombination aus kaufmännischen Tätigkeiten und dem technischen Umfeld spricht mich an. Wäre ein Praktikum vorab möglich?',
        createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        status: 'unread',
    },
    {
        id: '2',
        studentName: 'Max Schmidt',
        studentEmail: 'max.schmidt@realschule.de',
        schoolType: 'Realschule',
        graduationYear: 2025,
        interestedJob: 'Mechatroniker/in',
        message: `Hallo, ich habe Ihr Profil auf ${APP_BRAND_NAME} gefunden und bin sehr interessiert an der Mechatroniker-Ausbildung. Ich habe bereits ein Praktikum im technischen Bereich gemacht und würde gerne mehr über die Ausbildungsinhalte erfahren.`,
        createdAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
        status: 'unread',
    },
    {
        id: '3',
        studentName: 'Sophie Weber',
        studentEmail: 'sophie.w@gymnasium.de',
        schoolType: 'Gymnasium',
        graduationYear: 2024,
        interestedJob: 'Fachinformatiker/in',
        message:
            'Guten Tag, als angehende Abiturientin mit Schwerpunkt Informatik suche ich eine anspruchsvolle Ausbildung. Ihr Unternehmen hat mich durch die modernen Technologien und Benefits überzeugt. Gibt es die Möglichkeit eines Vorstellungsgesprächs?',
        createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
        status: 'read',
    },
    {
        id: '4',
        studentName: 'Tim Hoffmann',
        studentEmail: 'tim.hoffmann@mittelschule.de',
        schoolType: 'Mittelschule',
        graduationYear: 2025,
        interestedJob: 'Elektroniker/in',
        message:
            'Hallo, ich interessiere mich für die Elektroniker-Ausbildung bei Ihnen. Können Sie mir mehr Informationen zu den Voraussetzungen schicken?',
        createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
        status: 'answered',
        answeredAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    },
    {
        id: '5',
        studentName: 'Anna Becker',
        studentEmail: 'anna.b@schule.de',
        schoolType: 'Realschule',
        graduationYear: 2024,
        interestedJob: 'Industriekaufmann/-frau',
        message:
            'Sehr geehrtes Team, ich habe mich für einen anderen Ausbildungsplatz entschieden. Vielen Dank für Ihr Interesse.',
        createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        status: 'archived',
    },
]

const InboxContext = createContext<InboxContextType | undefined>(undefined)

export const InboxProvider = ({ children }: { children: ReactNode }) => {
    const [messages, setMessages] = useState<InboxMessage[]>(() => {
        if (typeof window === 'undefined') return mockMessages
        const stored = localStorage.getItem('inboxMessages')
        return stored ? JSON.parse(stored) : mockMessages
    })

    useEffect(() => {
        localStorage.setItem('inboxMessages', JSON.stringify(messages))
    }, [messages])

    const unreadCount = messages.filter((m) => m.status === 'unread').length

    const updateMessageStatus = (id: string, status: MessageStatus) => {
        setMessages((prev) =>
            prev.map((msg) =>
                msg.id === id
                    ? {
                          ...msg,
                          status,
                          answeredAt: status === 'answered' ? new Date().toISOString() : msg.answeredAt,
                      }
                    : msg,
            ),
        )
    }

    const getMessagesByStatus = (status: MessageStatus) => {
        return messages.filter((m) => m.status === status)
    }

    return (
        <InboxContext.Provider value={{ messages, unreadCount, updateMessageStatus, getMessagesByStatus }}>
            {children}
        </InboxContext.Provider>
    )
}

export const useInbox = () => {
    const context = useContext(InboxContext)
    if (context === undefined) {
        throw new Error('useInbox must be used within an InboxProvider')
    }
    return context
}
